<template>
  <Drawer
      v-model:visible="shouldShowInstallationBanner"
      position="top"
      class="h-auto w-full dark:bg-zinc-800 bg-zinc-50 border-0 border-b dark:border-zinc-800 border-zinc-300 shadow-none"
      :modal="false"
      :dismissable="false"
      @hide="setInstallBannerCookie"
      :block-scroll="false"
  >
    <template #container="{ closeCallback }">
      <div class="flex flex-row md:justify-between justify-center items-center lg:gap-6 gap-4 p-3 lg:px-5">
        <span class="md:hidden">
             <i
                 class="fal fa-times dark:text-neutral-400 text-xl cursor-pointer md:hidden"
                 @click="hideInstallationBanner(closeCallback)"
             ></i>
        </span>
        <div class="lg:size-14 size-12 rounded-xl bg-white shrink-0 overflow-hidden">
          <img
              src="/pwa-512x512.png"
              :alt="$t('label.app_icon')"
              class="block w-full h-full object-cover rounded-xl scale-110"
          />
        </div>
        <div class="flex flex-row items-center gap-3">
          <div class="lg:text-base text-sm dark:text-neutral-300 mr-4">
            {{ $t('message.install_banner_text') }}
          </div>
          <Button
              :label="$t('label.install')"
              class="px-10 dark:bg-sky-600 bg-sky-500 dark:hover:bg-sky-500 hover:bg-sky-400 border-0 rounded text-white"
              @click="triggerInstallation"
          />
          <Button
              :label="$t('label.not_now')"
              class="md:block hidden px-10 dark:text-neutral-400 border-none dark:hover:bg-[#191a1e] hover:bg-gray-200 active:bg-none rounded"
              outlined
              @click="hideInstallationBanner(closeCallback)"
          />
        </div>

        <div class="md:block hidden">
          <i
              class="fal fa-times dark:text-neutral-400 text-xl cursor-pointer p-2"
              @click="hideInstallationBanner(closeCallback)"
          ></i>
        </div>
      </div>
    </template>
  </Drawer>

  <template v-if="! isDesktop">
    <Drawer
        v-model:visible="showInstallation"
        position="bottom"
        class="h-auto max-w-[450px] dark:bg-zinc-900 w-full rounded-t-3xl px-3 pb-[calc(1rem+env(safe-area-inset-bottom))] border-0 border-t border-zinc-800 pt-2"
        :block-scroll="false"
        :pt="{'header': { style: 'display: none;' }}"
    >
      <div class="dark:text-neutral-300">
        <div class="flex flex-col gap-4 pt-5">
          <template v-if="platform === 'ios' && isSafari">
            <div class="flex flex-row items-end justify-between text-neutral-200">
              <div class="font-semibold text-xl">{{ $t('label.install_application') }}</div>
              <i class="fal fa-times text-2xl cursor-pointer mr-2" @click="showInstallation = false"></i>
            </div>
            <div
                class="flex items-center gap-4 px-5 w-full h-[100px] rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800"
            >
              <div class="size-16 rounded-2xl overflow-hidden">
                <img
                    src="/pwa-512x512.png"
                    :alt="$t('label.app_icon')"
                    class="block w-full h-full object-cover rounded-2xl scale-110"
                />
              </div>
              <div class="flex flex-col gap-1">
                <div class="font-semibold text-lg">Avenue44</div>
                <div class="text-xs">{{ origin }}</div>
              </div>
            </div>
            <div class="flex flex-col gap-4 px-1 pt-3 text-sm">
              <div class="flex flex-row items-center gap-2">
                <div class="font-semibold mr-1">1.</div>
                <div>{{ $t('label.click_on') }}</div>
                <div
                    class="p-1 rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800 size-8 shrink-0 grow-0"
                >
                  <img :src="exportAndSaveIcon" :alt="$t('label.export_and_save')" />
                </div>
              </div>
              <div class="flex flex-row items-center gap-2 whitespace-nowrap text-sm">
                <div class="font-semibold mr-1">2.</div>
                <div>{{ $t('label.scroll_and_select') }}</div>
                <div
                    class="p-1 rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800"
                >
                  {{ $t('label.add_to_home_screen') }}
                </div>
              </div>
              <div class="flex flex-row text-sm">
                <div class="flex gap-2 items-center text-sm">
                  <div class="font-semibold mr-1">3.</div>
                  <div class="whitespace-nowrap">
                    {{ $t('label.go_to_the') }}
                  </div>
                  <div class="size-8 rounded-lg overflow-hidden">
                    <img
                        src="/pwa-512x512.png"
                        :alt="$t('label.app_icon')"
                        class="block w-full h-full object-cover rounded-lg scale-110"
                    />
                  </div>
                  <div class="whitespace-nowrap">
                    {{ $t('label.icon_on_your_home_screen') }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="! isInstallable">
            <div class="flex flex-row items-end justify-between text-neutral-200">
              <div class="font-semibold text-xl">{{ $t('title.browser_change_required') }}</div>
              <i class="fal fa-times text-2xl cursor-pointer mr-2" @click="showInstallation = false"></i>
            </div>
            <div class="flex flex-col gap-5 pt-5">
              <div
                  class="flex items-center gap-3 px-5 w-full h-[100px] rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800"
              >
                <div class="size-[4.4rem]">
                  <img :src="platform === 'ios' ? safariIcon : chromeIcon" :alt="$t('label.logo')" class="p-2" />
                </div>
                <div class="flex flex-col gap-1">
                  <div class="font-semibold text-lg">Avenue44</div>
                  <div class="text-xs">{{ origin }}</div>
                </div>
              </div>
              <div class="text-center">
                {{ $t('message.browser_not_supported_prefix') }}
                <span class="font-semibold">{{ platform === 'ios' ? 'Safari' : 'Chrome' }}</span>
                {{ $t('message.browser_not_supported_suffix') }}
              </div>
              <Button
                  :label="linkCopied ? $t('label.link_copied') : $t('label.copy_link')"
                  :icon="linkCopied ? 'far fa-check' : 'far fa-copy'"
                  class="mt-3 border-0"
                  @click="copyLink"
              />
            </div>
          </template>

          <template v-else>
            <div class="flex flex-row items-end justify-between text-neutral-200">
              <div
                  class="font-semibold text-xl"
              >{{ isAppInstalled ? $t('label.app_installed') : $t('label.install_application') }}
              </div>
              <i class="fal fa-times text-2xl cursor-pointer mr-2" @click="showInstallation = false"></i>
            </div>
            <div
                class="flex items-center gap-4 px-5 w-full h-[100px] rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800"
            >
              <ProgressSpinner
                  v-if="loading"
                  class="size-16"
                  strokeWidth="4"
                  animation-duration="1.2s"
              />
              <div
                  v-else-if="! isAppInstalled"
                  class="size-16 rounded-2xl overflow-hidden"
              >
                <img
                    src="/pwa-512x512.png"
                    :alt="$t('label.app_icon')"
                    class="block w-full h-full object-cover rounded-2xl scale-110"
                />
              </div>
              <div
                  v-else
                  class="flex items-center justify-center size-16 shrink-0 grow-0 rounded-full border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-blue-400"
              >
                <i class="far fa-check text-3xl"></i>
              </div>
              <div class="flex flex-col gap-1">
                <div class="font-semibold text-lg">Avenue44</div>
                <div class="text-xs">{{ origin }}</div>
              </div>
            </div>

            <div
                v-if="! loading && ! isAppInstalled"
                class="flex items-center justify-center flex-col gap-4 px-5 h-[200px] border-2 border-dashed border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800 cursor-pointer"
                @click="showInstallPrompt"
            >
              <i class="fal fa-download text-2xl" />
              <div class="font-semibold">
                {{ $t('label.tap_to_install') }}
              </div>
            </div>
            <div v-if="isAppInstalled" class="flex gap-2 items-center text-sm mt-5">
              <div class="whitespace-nowrap">
                {{ $t('label.search_for_the') }}
              </div>
              <div class="size-7 shrink-0 grow-0 rounded-lg border border-neutral-200 bg-white">
                <img src="/pwa-512x512.png" :alt="$t('label.app_icon')" class="p-1" />
              </div>
              <div class="whitespace-nowrap">
                {{ $t('label.icon_on_your_home_screen') }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </Drawer>
  </template>

  <template v-if="isDesktop">
    <Dialog
        v-model:visible="showInstallation"
        modal
        :header="$t('title.browser_change_required')"
        class="dark:bg-zinc-900 dark:border-zinc-800 w-[34rem] px-3 pt-5"
        :draggable="false"
        :show-header="false"
        @hide="linkCopied = false"
    >
      <div v-if="isInstallable" class="dark:text-neutral-300">
        <div class="flex flex-row items-end justify-between">
          <div class="font-semibold text-xl">{{ $t('label.install_application') }}</div>
          <i class="fal fa-times text-2xl cursor-pointer" @click="showInstallation = false"></i>
        </div>
        <div class="flex flex-col gap-4 pt-5">
          <div
              class="flex items-center gap-4 px-5 w-full h-[100px] rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800"
          >
            <div class="size-16 rounded-2xl   overflow-hidden">
              <img
                  src="/pwa-512x512.png"
                  :alt="$t('label.app_icon')"
                  class="block w-full h-full object-cover rounded-2xl scale-110"
              />
            </div>
            <div class="flex flex-col gap-1">
              <div class="font-semibold text-lg">Avenue44</div>
              <div class="text-xs">{{ origin }}</div>
            </div>
          </div>

          <div
              v-if="! isAppInstalled"
              class="flex items-center justify-center flex-col gap-4 px-5 h-[200px] border-2 border-dashed border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800 cursor-pointer"
              @click="showInstallPrompt"
          >
            <ProgressSpinner
                v-if="loading"
                class="size-16"
                strokeWidth="4"
                animation-duration="1.2s"
            />
            <template v-else>
              <i class="fal fa-download text-2xl" />
              <div class="font-semibold">
                {{ $t('label.tap_to_install') }}
              </div>
            </template>

          </div>
          <div v-if="isAppInstalled" class="flex gap-2 items-center text-sm mt-5">
            <div class="whitespace-nowrap">
              {{ $t('label.search_for_the') }}
            </div>
            <div class="size-7 shrink-0 grow-0 rounded-lg  overflow-hidden">
              <img
                  src="/pwa-512x512.png"
                  :alt="$t('label.app_icon')"
                  class="block w-full h-full object-cover rounded-lg scale-110"
              />
            </div>
            <div class="whitespace-nowrap">
              {{ $t('label.icon_on_your_home_screen') }}
            </div>
          </div>
        </div>
      </div>

      <div v-else class="dark:text-neutral-300">
        <div class="flex flex-row items-end justify-between">
          <div class="font-semibold text-xl">{{ $t('title.browser_change_required') }}</div>
          <i class="fal fa-times text-2xl cursor-pointer" @click="showInstallation = false"></i>
        </div>
        <div class="flex flex-col gap-5 pt-5">
          <div
              class="flex items-center gap-3 px-5 w-full h-[100px] rounded border border-zinc-200 dark:border-zinc-700 bg-neutral-100 dark:bg-zinc-800"
          >
            <div class="size-[4.4rem]">
              <img :src="chromeIcon" :alt="$t('label.logo')" class="p-2" />
            </div>
            <div class="flex flex-col gap-1">
              <div class="font-semibold text-lg">Avenue44</div>
              <div class="text-xs">{{ origin }}</div>
            </div>
          </div>
          <div class="text-center">
            {{ $t('message.browser_not_supported_prefix') }}
            <span class="font-semibold">Chrome</span>
            {{ $t('message.browser_not_supported_suffix') }}
          </div>
          <Button
              :label="linkCopied ? $t('label.link_copied') : $t('label.copy_link')"
              :icon="linkCopied ? 'far fa-check' : 'far fa-copy'"
              class="mt-3 border-0"
              @click="copyLink"
          />
        </div>
      </div>
    </Dialog>
  </template>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { Drawer } from 'primevue';

const exportAndSaveIcon = '/export_and_save_icon.svg'
const chromeIcon = '/chrome-icon.svg'
const safariIcon = '/safari-icon.svg'

export default {
  name: 'InstallPopup',

  components: {
    Drawer,
  },

  props: {
    variant: {
      type: String,
      default: 'button',
      validator(value) {
        return ['button', 'top-drawer'].includes(value)
      }
    }
  },

  data() {
    return {
      showInstallation: false,
      showInstallationBanner: false,
      exportAndSaveIcon: exportAndSaveIcon,
      chromeIcon: chromeIcon,
      safariIcon: safariIcon,
      isAppInstalled: globalThis.matchMedia('(display-mode: standalone)').matches,
      isInstalledApp: globalThis.matchMedia('(display-mode: standalone)').matches,
      loading: false,
      origin: globalThis.location.origin.replace(/^https?:\/\//, ''),
      linkCopied: false,
    }
  },

  computed: {
    ...mapGetters([
      'installPrompt',
      'installPromptEligible',
    ]),

    shouldShowInstallationBanner: {
      get() {
        if (this.$cookies.get('app-install-banner') === 'hide') {
          return false;
        }

        if (this.$cookies.get('app-installed') === 'installed') {
          return false;
        }

        return this.showInstallationBanner;
      },
      set(value) {
        this.showInstallationBanner = value;
      }
    },

    platform() {
      const userAgent = navigator.userAgent || globalThis.opera;

      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iPad|iPhone|iPod/.test(userAgent) && ! globalThis.MSStream) {
        return 'ios';
      }

      return null;
    },

    isSafari() {
      return /^(?:(?!chrome|android|crios|fxios|opera|opt).)*safari(?:(?!opera|opt).)*$/i.test(navigator.userAgent);
    },

    isDesktop() {
      if (navigator.userAgentData?.mobile !== undefined) {
        return ! navigator.userAgentData.mobile;
      }

      const userAgent = navigator.userAgent || globalThis.opera;

      if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
        return false;
      }

      return globalThis.innerWidth > 1024;
    },

    isInstallable() {
      return Boolean(this.installPrompt) || this.installPromptEligible;
    },
  },

  watch: {
    installPrompt(newPrompt) {
      if (! newPrompt) {
        return;
      }

      this.$cookies.remove('app-installed');

      if (this.$route.query['xlwpvznko'] === 'install') {
        this.showInstallation = true;

        return;
      }

      this.showInstallationBanner = true;
    },

    installPromptEligible(newValue, oldValue) {
      if (! oldValue || newValue) {
        return;
      }

      this.$cookies.set('app-installed', 'installed', '7d');
      this.$emitter.emit('app-installed');

      if (this.isDesktop) {
        this.showInstallation = false;
      }

      setTimeout(() => {
        this.loading = false;
        this.isAppInstalled = true;
      }, 1500);
    },
  },

  methods: {
    ...mapActions([
      'setInstallPrompt',
    ]),

    async showInstallPrompt() {
      if (! this.installPrompt) {
        return;
      }

      this.loading = true;

      this.installPrompt.prompt();

      const { outcome } = await this.installPrompt.userChoice;

      if (outcome === 'accepted') {
        await this.setInstallPrompt(null);

        return;
      }

      this.loading = false;
    },

    triggerInstallation() {
      this.showInstallationBanner = false;
      this.showInstallation = true;
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(globalThis.location.origin + '/?xlwpvznko=install');

        this.linkCopied = true;
      } catch (error) {
        console.error(error);
      }
    },

    setInstallBannerCookie() {
      this.$cookies.set('app-install-banner', 'hide', '7d');
    },

    hideInstallationBanner(closeCallback) {
      this.setInstallBannerCookie();

      this.$emitter.emit('hide-install-banner');

      closeCallback();
    }
  },

  created() {
    this.$emitter.on('install-button-click', () => this.triggerInstallation());
  },

  async mounted() {
    if (this.$cookies.get('app-installed') === 'installed') {
      this.$cookies.set('app-installed', 'installed', '7d');
    }

    if (this.isInstallable) {
      this.showInstallationBanner = true;
      this.$cookies.remove('app-installed');
    }

    await this.$router.isReady();

    if (! this.isInstalledApp && this.platform === 'ios' && this.$route.query['xlwpvznko'] === 'install') {
      this.showInstallation = true;
    }

    if (! this.isInstalledApp && this.platform === 'ios' && ! this.showInstallation) {
      this.showInstallationBanner = true;
    }
  },
}
</script>
